/**
 * Utilidades de bajo nivel sobre GameState.
 *
 * Las comparten `combatEngine` y `effects`; viven aparte para que no se importen
 * entre sí en círculo.
 *
 * Convenio de todo el motor: el estado es inmutable de puertas afuera. Cada
 * función que cambia algo clona primero y devuelve el clon. Con un estado tan
 * pequeño (3 zonas, ~20 cartas) el coste de `structuredClone` es irrelevante al
 * lado de la clase de bugs que evita — y Zustand necesita una referencia nueva
 * para repintar.
 */

import type { BoardCard, CardInstance, GameState, PlayerSide, Zone, ZoneIndex } from '@/types/game';
import { MAX_HAND_SIZE } from '@/types/game';
import type { RngState } from './rng';

/** Copia profunda del estado. Punto de entrada de toda mutación del motor. */
export function cloneState(state: GameState): GameState {
  return structuredClone(state);
}

/** El bando contrario. */
export function opponentOf(side: PlayerSide): PlayerSide {
  return side === 'PLAYER' ? 'OPPONENT' : 'PLAYER';
}

/** Vista del RNG que guarda el estado. */
export function rngOf(state: GameState): RngState {
  return { seed: state.seed, cursor: state.rngCursor };
}

/** Escribe de vuelta el cursor tras consumir azar. Muta: solo para uso interno. */
export function commitRng(state: GameState, rng: RngState): void {
  state.rngCursor = rng.cursor;
}

/** Todas las cartas del tablero, de ambos bandos. */
export function allBoardCards(state: GameState): BoardCard[] {
  return state.zones.flatMap((zone) => zone.cards);
}

/** Busca una carta del tablero por su instanceId. */
export function findBoardCard(state: GameState, instanceId: string): BoardCard | undefined {
  for (const zone of state.zones) {
    const found = zone.cards.find((card) => card.instanceId === instanceId);
    if (found) return found;
  }
  return undefined;
}

/** Cartas de un bando en una zona, en orden de colocación. */
export function cardsOf(zone: Zone, side: PlayerSide): BoardCard[] {
  return zone.cards
    .filter((card) => card.owner === side)
    .sort((a, b) => a.playOrder - b.playOrder);
}

/** Solo las ya reveladas: son las únicas que puntúan y las únicas que ve el rival. */
export function revealedCardsOf(zone: Zone, side: PlayerSide): BoardCard[] {
  return cardsOf(zone, side).filter((card) => card.revealed);
}

/**
 * Roba `amount` cartas. Si el mazo se agota o la mano está llena, roba lo que
 * pueda y sigue: quedarse sin mazo no es un error, es una situación de juego.
 * Muta el estado recibido (que siempre es un clon).
 *
 * Devuelve cuántas cartas se robaron de verdad, para poder contarlo en el log.
 */
export function drawCards(state: GameState, side: PlayerSide, amount: number): number {
  const player = state.players[side];
  let drawn = 0;
  for (let i = 0; i < amount; i += 1) {
    if (player.deck.length === 0 || player.hand.length >= MAX_HAND_SIZE) break;
    const card = player.deck.shift();
    if (!card) break;
    player.hand.push(card);
    drawn += 1;
  }
  return drawn;
}

/** Añade una línea al registro de combate que lee el jugador. */
export function pushLog(state: GameState, message: string): void {
  state.log.push(`T${state.turn} · ${message}`);
}

/** Índices de las otras dos zonas. */
export function otherZoneIndices(index: ZoneIndex): ZoneIndex[] {
  return ([0, 1, 2] as ZoneIndex[]).filter((i) => i !== index);
}

/** Crea el ejemplar de carta que va al mazo de una partida local. */
export function makeCardInstance(
  masterId: string,
  owner: PlayerSide,
  level: number,
  serial: number,
): CardInstance {
  return {
    // En la Fase 1 el id solo tiene que ser único dentro de la partida; en la
    // Fase 2 lo sustituye el UUID real de user_cards.
    instanceId: `${owner.toLowerCase()}-${serial}-${masterId}`,
    masterId,
    level,
    owner,
  };
}
